import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ValidatorFlags, GCValidatorFn } from "src/app/modules/ngx-components";
import { DraftService } from 'src/app/modules/ngx-draft';
import { DateTime } from "luxon";
import IBAN from "iban";

export class GCValidators {
    static required: ValidatorFn;

    static {
        const required = (control: AbstractControl): ValidationErrors | null => Validators.required(control);
        required.flags = ValidatorFlags.required;
        this.required = required;
    }

    static conditional(condition: (form: FormGroup) => boolean, validators: ValidatorFn | ValidatorFn[]): ValidatorFn {
        const val = (control: AbstractControl): ValidationErrors | null => {
            if (condition(<FormGroup>control.root)) {
                if (Array.isArray(validators))
                    return Validators.compose(validators)(control);
                return validators(control);
            }
            return null;
        };
        Object.defineProperty(val, 'flags', {
            enumerable: true,
            get() {
                if (!condition(<FormGroup<unknown>><unknown>DraftService.instance.form))
                    return ValidatorFlags.none;
                if (!Array.isArray(validators))
                    return (<GCValidatorFn>validators).flags ?? ValidatorFlags.none;
                // eslint-disable-next-line no-bitwise
                return (<GCValidatorFn[]>validators).reduce((f,v) => f | v.flags ,ValidatorFlags.none);
            }
        });
        return <GCValidatorFn>val;
    }

    static byValue(path: string[], value: unknown, validators: ValidatorFn | ValidatorFn[]): ValidatorFn {
        const val = (control: AbstractControl): ValidationErrors | null => {
            if (control.root.get(path).value === value) {
                if (Array.isArray(validators))
                    return Validators.compose(validators)(control);
                return validators(control);
            }
            return null;
        };
        Object.defineProperty(val, 'flags', {
            enumerable: true,
            get() {
                if (DraftService.instance.form.root.get(path).value !== value)
                    return ValidatorFlags.none;
                if (!Array.isArray(validators))
                    return (<GCValidatorFn>validators).flags ?? ValidatorFlags.none;
                // eslint-disable-next-line no-bitwise
                return (<GCValidatorFn[]>validators).reduce((f,v) => f | v.flags ,ValidatorFlags.none);
            }
        });
        return <GCValidatorFn>val;
    }

    static notByValue(path: string[], value: unknown, validators: ValidatorFn | ValidatorFn[]): ValidatorFn {
        const val = (control: AbstractControl): ValidationErrors | null => {
            if (control.root.get(path).value !== value) {
                if (Array.isArray(validators))
                    return Validators.compose(validators)(control);
                return validators(control);
            }
            return null;
        };
        Object.defineProperty(val, 'flags', {
            enumerable: true,
            get() {
                if (DraftService.instance.form.root.get(path).value === value)
                    return ValidatorFlags.none;
                if (!Array.isArray(validators))
                    return (<GCValidatorFn>validators).flags ?? ValidatorFlags.none;
                // eslint-disable-next-line no-bitwise
                return (<GCValidatorFn[]>validators).reduce((f,v) => f | v.flags ,ValidatorFlags.none);
            }
        });
        return <GCValidatorFn>val;
    }

    static arrayLength(max?: number, min?: number): ValidatorFn {
        const res = (control: AbstractControl): ValidationErrors | null => {
            if (min && (!control.value || control.value.length < min))
                return { arrayLength: 'min' };
            if (max && control.value?.length > max)
                return { arrayLength: 'max' };
            return null;
        };
        Object.defineProperty(res, 'flags', {enumerable: false, writable: false, value: ValidatorFlags.none});
        return <GCValidatorFn>res;
    }

    /**
     * Like `Validators.maxLength`, but counts UTF-8 bytes instead of UTF-16 code units.
     * Use it wherever the backend limit is given in bytes (KiB/MiB), otherwise non-ASCII
     * input passes the client check and is rejected by the backend.
     */
    static maxByteLength(max: number): ValidatorFn {
        const res = (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (value === null || value === undefined || value === '')
                return null;
            const actualLength = new TextEncoder().encode(String(value)).length;
            if (actualLength > max)
                return { maxlength: { requiredLength: max, actualLength } };
            return null;
        };
        Object.defineProperty(res, 'flags', {enumerable: false, writable: false, value: ValidatorFlags.none});
        return <GCValidatorFn>res;
    }

    static beforeDateTime(before: DateTime): ValidatorFn {
        const res = (control: AbstractControl): ValidationErrors | null => {
            if (!control.value || before.diff(control.value).valueOf() < 0)
                return { dateTimeBefore: true };
            return null;
        };
        Object.defineProperty(res, 'flags', {enumerable: false, writable: false, value: ValidatorFlags.none});
        return <GCValidatorFn>res;
    }

    static notEqual(value: unknown): ValidatorFn {
        const res = (control: AbstractControl): ValidationErrors | null => {
            if (control.value === value)
                return { invalid: true };
            return null;
        };
        Object.defineProperty(res, 'flags', {enumerable: false, writable: false, value: ValidatorFlags.none});
        return <GCValidatorFn>res;
    }

    static valuesInOrder(): ValidatorFn {
      const res = (control: AbstractControl): ValidationErrors | null => {
            const errors: ValidationErrors = {};
            const value = control.value as unknown[];
            if (!!value?.length) {
              for (let i = 0; i < value.length; i++) {
                if (!value[i]) {
                  errors.unset = true;
                  break;
                }
                if (i > 0 && value[i] <= value[i - 1]) {
                  errors.order = true;
                  break;
                }
              }
            }
            if (!Object.keys(errors).length) return undefined;
            return errors as ValidationErrors;
        };
        Object.defineProperty(res, 'flags', {enumerable: false, writable: false, value: ValidatorFlags.none});
        return <GCValidatorFn>res;
    }

    /**
     * checks for a number to be an exact fraction
     *
     * @param fraction
     *
     * @example
     *  2 will allow 10; 10,5; 11 ...
     * 10 will allow one 10,1; 10,2 ...
     */
    static fractional(fraction: number): ValidatorFn {
        const res = (control: AbstractControl): ValidationErrors | null => {
            if (Math.abs(control.value - Math.floor(control.value * fraction) / fraction) > Number.EPSILON)
                return { wrongNumber: true };
            return null;
        };
        Object.defineProperty(res, 'flags', {enumerable: false, writable: false, value: ValidatorFlags.none});
        return <GCValidatorFn>res;
    }

    // example async validation
    // static ibanAsync(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    //     return control.valueChanges.pipe(
    //         debounceTime(500),
    //         distinctUntilChanged(),
    //         switchMap((value) => from(AppService.instance.api.CashflowWorker.isIbanValid(value))),
    //         catchError(e => { e.displayErrorToast(); return of(false); }),
    //         map((valid: boolean) => (valid ? null : {ibanInvalid: true})),
    //         first(),
    //     );
    // }

    static iban(control: AbstractControl): ValidationErrors {
        if (!control?.value || !IBAN.isValid(control.value))
            return { ibanInvalid: true };
        return null;
    }

    static uniqueRuntime(control: AbstractControl): ValidationErrors {
      if ((control.root as FormGroup).controls) {
        const formArray = (control.root as FormGroup).controls.runtimes as FormArray;
        const runtimeValue = (control.parent as FormGroup).get('value')?.value;
        const runtimeUnit = (control.parent as FormGroup).get('unit')?.value;
        if (runtimeValue === undefined || runtimeUnit === undefined || control.pristine)
          return {};
        const duplicates = formArray.controls.filter((group: AbstractControl): boolean => {
          const otherValue = group.get('runtime.value')?.value;
          const otherUnit = group.get('runtime.unit')?.value;
          return otherValue === runtimeValue && otherUnit === runtimeUnit;
        }).length;
        return duplicates > 0 ? { 'uniqueRuntime': 'Runtime combination must be unique' } : {};
      }
    }

  static ipCidrValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
    if (typeof value !== "string") return { ipCidr: { message: 'IP_CIDR_ERRORS.INVALID_IP_CIDR_FORMAT' } };
    const entries = value.split(';').map((entry: string) => entry.trim()).filter((entry: string) => entry.length > 0);
    if (entries.length === 0)
      return { ipCidr: { message: 'IP_CIDR_ERRORS.NO_ENTRY' } };
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const error = GCValidators.validateSingleIpCidr(entry);
      if (error) {
        return {
          ipCidr: {
            message:  `${'IP_CIDR_ERRORS.ENTRY'} ${i + 1}: ${error}`,
            entry:    entry,
            position: i + 1
          }
        };
      }
    }
    return null;
  };

  static validateSingleIpCidr(entry: string): string | null {
    const parts = entry.split('/');
    if (parts.length !== 2)
      return 'IP_CIDR_ERRORS.INVALID_IP_CIDR_FORMAT';
    const [ip, cidrStr] = parts;
    const ipError = GCValidators.validateIPv4Components(ip.trim());
    if (ipError) return ipError;
    const cidr = parseInt(cidrStr.trim(), 10);
    if (isNaN(cidr) || cidr < 0 || cidr > 32)
      return 'IP_CIDR_ERRORS.INVALID_CIDR';
    const ipRangeError = GCValidators.validateIPRange(ip.trim());
    if (ipRangeError) return ipRangeError;
    const networkError = GCValidators.validateNetworkAddressOptimized(ip.trim(), cidr);
    if (networkError) return networkError;
    return null;
  }

  static validateIPv4Components(ip: string): string | null {
    const octets = ip.split('.');
    if (octets.length !== 4) return 'IP_CIDR_ERRORS.INVALID_IP_FORMAT';
    for (const octet of octets) {
      const num = parseInt(octet, 10);
      if (octet !== num.toString() || num > 255)
        return 'IP_CIDR_ERRORS.INVALID_OCTET';
    }
    return null;
  }

  static validateIPRange(ip: string): string | null {
    const octets = ip.split('.').map(octet => parseInt(octet, 10));
    const [a, b, c, d] = octets;
    // Private networks (RFC 1918)
    if (a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31))
      return 'IP_CIDR_ERRORS.NO_PRIVATE_NETWORKS';
    // CGN Private range (RFC 6598)
    if (a === 100 && b >= 64 && b <= 127)
      return 'IP_CIDR_ERRORS.NO_CGN_NETWORKS';
    // Multicast (224.0.0.0 to 239.255.255.255)
    if (a >= 224 && a <= 239)
      return 'IP_CIDR_ERRORS.NO_MULTICAST_ADDRESS';
    // Class E / Reserved (240.0.0.0 to 255.255.255.255)
    if (a >= 240)
      return 'IP_CIDR_ERRORS.NO_RESERVED_ADDRESS';
    // Loopback (127.0.0.0/8)
    if (a === 127)
      return 'IP_CIDR_ERRORS.NO_LOOPBACK_ADDRESS';
    // Link-local (169.254.0.0/16)
    if (a === 169 && b === 254)
      return 'IP_CIDR_ERRORS.NO_LINK_LOCAL_ADDRESS';
    // Broadcast (255.255.255.255)
    if (a === 255 && b === 255 && c === 255 && d === 255)
      return 'IP_CIDR_ERRORS.NO_BROADCAST_ADDRESS';
    // Network address 0.0.0.0
    if (a === 0 && b === 0 && c === 0 && d === 0)
      return 'IP_CIDR_ERRORS.NO_ZERO_ADDRESS';
    return null;
  }

  static validateNetworkAddressOptimized(ip: string, cidr: number): string | null {
    const ipInt = GCValidators.ipToInteger(ip);
    const hostMask = cidr === 32 ? 0 : (0xFFFFFFFF >>> cidr);
    const hostBits = (ipInt & hostMask) >>> 0;
    if (hostBits !== 0)
      return 'IP_CIDR_ERRORS.NETWORK_IP';
    return null;
  }

  static ipToInteger(ip: string): number {
    const octets = ip.split('.').map(octet => parseInt(octet, 10));
    return ((octets[0] << 24) | (octets[1] << 16) | (octets[2] << 8) | octets[3]) >>> 0;
  }
}
